import React from "react";
import { Table, Button, Tag, Space, message, Modal, Input, Select, Form, Typography } from "antd";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getInvites, createInvite, checkInvite } from "./api";
import { CopyOutlined, PlusOutlined, EyeOutlined } from "@ant-design/icons";

const { Title } = Typography;
const { Option } = Select;

interface Invite {
  id: number;
  code: string;
  email: string | null;
  status: 'pending' | 'used' | 'expired';
  expiresAt: string;
  createdAt: string;
  usedAt: string | null;
  invitedBy: {
    id: number;
    email: string;
    firstName: string | null;
    lastName: string | null;
  };
  invitedUser: {
    id: number;
    email: string;
    firstName: string | null;
    lastName: string | null;
    createdAt: string;
  } | null;
}

export default function InvitesTable() {
  const queryClient = useQueryClient();
  const [isCreateModalVisible, setIsCreateModalVisible] = React.useState(false);
  const [isCheckModalVisible, setIsCheckModalVisible] = React.useState(false);
  const [checkCode, setCheckCode] = React.useState("");
  const [checkResult, setCheckResult] = React.useState<any>(null);
  const [form] = Form.useForm();

  const { data: invites, isLoading } = useQuery({
    queryKey: ['invites'],
    queryFn: getInvites,
  });

  const createInviteMutation = useMutation({
    mutationFn: createInvite,
    onSuccess: () => {
      message.success("Инвайт успешно создан");
      queryClient.invalidateQueries({ queryKey: ['invites'] });
      setIsCreateModalVisible(false);
      form.resetFields();
    },
    onError: (error: any) => {
      message.error(error.message || "Ошибка создания инвайта");
    },
  });

  const checkInviteMutation = useMutation({
    mutationFn: checkInvite,
    onSuccess: (data) => {
      setCheckResult(data);
      message.success("Инвайт проверен");
    },
    onError: (error: any) => {
      message.error(error.message || "Ошибка проверки инвайта");
      setCheckResult(null);
    },
  });

  const handleCreateInvite = (values: any) => {
    createInviteMutation.mutate(values);
  };

  const handleCheckInvite = () => {
    if (!checkCode.trim()) {
      message.error("Введите код инвайта");
      return;
    }
    checkInviteMutation.mutate(checkCode);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    message.success("Скопировано в буфер обмена");
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'green';
      case 'used':
        return 'blue';
      case 'expired':
        return 'red';
      default:
        return 'default';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending':
        return 'Активен';
      case 'used':
        return 'Использован';
      case 'expired':
        return 'Истек';
      default:
        return status;
    }
  };

  const columns = [
    {
      title: "Код",
      dataIndex: "code",
      key: "code",
      width: 150,
      render: (code: string) => (
        <div style={{ fontFamily: 'monospace', fontSize: 12 }}>
          {code}
        </div>
      ),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (email: string | null) => email || "-",
    },
    {
      title: "Статус",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>
          {getStatusText(status)}
        </Tag>
      ),
    },
    {
      title: "Создан",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 120,
      render: (date: string) => new Date(date).toLocaleDateString('ru-RU'),
    },
    {
      title: "Истекает",
      dataIndex: "expiresAt",
      key: "expiresAt",
      width: 120,
      render: (date: string) => new Date(date).toLocaleDateString('ru-RU'),
    },
    {
      title: "Создал",
      dataIndex: "invitedBy",
      key: "invitedBy",
      render: (invitedBy: any) => (
        <div>
          <div>{invitedBy.email}</div>
          <div style={{ fontSize: 12, color: '#666' }}>
            {invitedBy.firstName} {invitedBy.lastName}
          </div>
        </div>
      ),
    },
    {
      title: "Использован",
      dataIndex: "invitedUser",
      key: "invitedUser",
      render: (invitedUser: any) => {
        if (!invitedUser) return "-";
        return (
          <div>
            <div>{invitedUser.email}</div>
            <div style={{ fontSize: 12, color: '#666' }}>
              {invitedUser.firstName} {invitedUser.lastName}
            </div>
            <div style={{ fontSize: 11, color: '#999' }}>
              {new Date(invitedUser.createdAt).toLocaleDateString('ru-RU')}
            </div>
          </div>
        );
      },
    },
    {
      title: "Действия",
      key: "actions",
      width: 120,
      render: (record: Invite) => (
        <Space>
          <Button
            size="small"
            icon={<CopyOutlined />}
            onClick={() => copyToClipboard(record.code)}
          >
            Копировать
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Title level={3} style={{ margin: 0, color: "#1976d2" }}>
          Инвайты
        </Title>
        <Space>
          <Button
            icon={<EyeOutlined />}
            onClick={() => setIsCheckModalVisible(true)}
          >
            Проверить инвайт
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setIsCreateModalVisible(true)}
            style={{
              background: "linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)",
              border: "none",
              borderRadius: 8,
              fontWeight: 600,
            }}
          >
            Создать инвайт
          </Button>
        </Space>
      </div>

      <Table
        dataSource={invites}
        columns={columns}
        loading={isLoading}
        rowKey="id"
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} из ${total} инвайтов`,
        }}
        scroll={{ x: 1200 }}
      />

      {/* Модальное окно создания инвайта */}
      <Modal
        title="Создать инвайт"
        open={isCreateModalVisible}
        onCancel={() => {
          setIsCreateModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={500}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreateInvite}
        >
          <Form.Item
            name="email"
            label="Email (опционально)"
            rules={[
              { type: 'email', message: 'Введите корректный email' }
            ]}
          >
            <Input placeholder="user@example.com" />
          </Form.Item>

          <Form.Item
            name="expiresIn"
            label="Срок действия"
            initialValue="7d"
          >
            <Select>
              <Option value="1h">1 час</Option>
              <Option value="1d">1 день</Option>
              <Option value="7d">7 дней</Option>
              <Option value="30d">30 дней</Option>
            </Select>
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={createInviteMutation.isPending}
                style={{
                  background: "linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)",
                  border: "none",
                }}
              >
                Создать
              </Button>
              <Button
                onClick={() => {
                  setIsCreateModalVisible(false);
                  form.resetFields();
                }}
              >
                Отмена
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Модальное окно проверки инвайта */}
      <Modal
        title="Проверить инвайт"
        open={isCheckModalVisible}
        onCancel={() => {
          setIsCheckModalVisible(false);
          setCheckCode("");
          setCheckResult(null);
        }}
        footer={null}
        width={600}
      >
        <div style={{ marginBottom: 16 }}>
          <Input
            placeholder="Введите код инвайта"
            value={checkCode}
            onChange={(e) => setCheckCode(e.target.value)}
            onPressEnter={handleCheckInvite}
            style={{ marginBottom: 16 }}
          />
          <Button
            type="primary"
            onClick={handleCheckInvite}
            loading={checkInviteMutation.isPending}
            style={{
              background: "linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)",
              border: "none",
            }}
          >
            Проверить
          </Button>
        </div>

        {checkResult && (
          <div style={{ 
            background: '#f6f8fa', 
            padding: 16, 
            borderRadius: 8,
            border: '1px solid #e1e4e8'
          }}>
            <Title level={5}>Результат проверки:</Title>
            <div style={{ marginBottom: 8 }}>
              <strong>Код:</strong> {checkResult.code}
            </div>
            <div style={{ marginBottom: 8 }}>
              <strong>Email:</strong> {checkResult.email || "Не указан"}
            </div>
            <div style={{ marginBottom: 8 }}>
              <strong>Истекает:</strong> {new Date(checkResult.expiresAt).toLocaleString('ru-RU')}
            </div>
            <div style={{ marginBottom: 8 }}>
              <strong>Пригласил:</strong> {checkResult.invitedBy.email}
            </div>
            <div style={{ marginBottom: 8 }}>
              <strong>Ссылка для регистрации:</strong>
            </div>
            <Input
              value={checkResult.registrationUrl}
              readOnly
              style={{ fontFamily: 'monospace', fontSize: 12 }}
            />
          </div>
        )}
      </Modal>
    </div>
  );
} 